import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { SeoHead } from '@/components/seo/SeoHead';

const ERROR_MESSAGES: Record<string, string> = {
  permission_denied: 'Permissões insuficientes da Meta. Reconecte e autorize todas as páginas e contas do Instagram, inclusive as do Business Manager.',
  no_professional_account: 'Nenhuma conta Instagram profissional encontrada nas páginas retornadas pela Meta. Verifique se a conta é Profissional e está vinculada à Página correta.',
  no_pages_found: 'A Meta não retornou nenhuma página para esta autorização. Reconecte e marque explicitamente as páginas corretas na tela de permissão.',
  account_already_connected: 'Esta conta já está ativa em outro workspace. Desconecte-a lá antes de reconectar aqui.',
  persist_failed: 'Não foi possível concluir a conexão agora. Tente novamente em alguns minutos. Se persistir, entre em contato com o suporte.',
};

const GENERIC_ERROR = 'Não foi possível concluir a conexão agora. Tente novamente em alguns minutos. Se persistir, entre em contato com o suporte.';

export default function InstagramOAuthResult() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processando...');
  const [reason, setReason] = useState<string | null>(null);
  const [igIds, setIgIds] = useState<string[]>([]);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  useEffect(() => {
    const success = searchParams.get('success') === '1';
    const username = searchParams.get('username') || '';
    const error = searchParams.get('error');
    const errorCode = searchParams.get('error_code');
    const reasonParam = searchParams.get('reason');
    const igIdsParam = searchParams.get('ig_ids');
    const errorDetailParam = searchParams.get('error_detail');

    if (success && username) {
      const usernames = username.split(',').map(u => `@${decodeURIComponent(u)}`).join(', ');
      setStatus('success');
      setMessage(`Conectado: ${usernames}`);

      // Send postMessage to opener window
      if (window.opener) {
        try {
          window.opener.postMessage({
            type: 'instagram-connected',
            username: username,
            success: true
          }, '*');
          console.log('Instagram postMessage sent successfully');
        } catch (e) {
          console.error('postMessage failed:', e);
        }
      }

      // Auto-close after 1.5 seconds
      setTimeout(() => {
        window.close();
      }, 1500);
    } else {
      setStatus('error');
      setReason(reasonParam);
      if (igIdsParam) {
        setIgIds(igIdsParam.split(',').filter(Boolean));
      }
      // We intentionally IGNORE errorDetailParam here — never show raw DB errors to clients.
      // Server logs (edge function console.error) are the source of truth for support.
      setErrorDetail(null);

      // Choose a friendly message:
      // - mapped error_code → mapped friendly text (account_already_connected, persist_failed, etc.)
      // - otherwise → generic friendly fallback (do NOT echo raw `error` query param)
      const displayMessage = (errorCode && ERROR_MESSAGES[errorCode])
        ? ERROR_MESSAGES[errorCode]
        : GENERIC_ERROR;
      setMessage(displayMessage);

      if (window.opener) {
        try {
          window.opener.postMessage({
            type: 'instagram-error',
            error: displayMessage,
            error_code: errorCode || 'unknown',
            reason: reasonParam || null,
            success: false
          }, '*');
        } catch (e) {
          console.error('postMessage failed:', e);
        }
      }
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <SeoHead title="Instagram | FabChat" noindex />
      <div className="bg-card border border-border rounded-2xl p-8 max-w-sm w-full text-center shadow-lg">
        <div className="text-2xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent mb-6">
          FabChat
        </div>

        {status === 'processing' && (
          <>
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
            <h1 className="text-lg font-semibold text-foreground mb-2">Processando...</h1>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-lg font-semibold text-foreground mb-2">Conectado com sucesso!</h1>
            <p className="text-sm text-muted-foreground mb-4">{message}</p>
            <p className="text-xs text-muted-foreground">Esta janela fechará automaticamente...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-lg font-semibold text-foreground mb-2">Erro na conexão</h1>
            <p className="text-sm text-muted-foreground mb-4 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
              {message}
            </p>

            {reason === 'orphan_ig' && (
              <div className="text-left text-xs text-muted-foreground mb-4 p-3 bg-muted rounded-lg border border-border space-y-2">
                <p className="font-semibold text-foreground">Por que isso aconteceu?</p>
                <p>
                  A Meta confirmou que você selecionou uma conta do Instagram, mas a Página do
                  Facebook vinculada não está visível para o nosso aplicativo. Isso geralmente
                  acontece quando:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>A Página vive dentro de um <span className="font-semibold">Meta Business Manager</span> e o seu usuário tem acesso só via Business (não admin direto da Página).</li>
                  <li>A Página foi criada pelo <span className="font-semibold">Instagram Profissional</span> sem uma Página clássica do Facebook vinculada.</li>
                </ul>
                {igIds.length > 0 && (
                  <p>Conta(s) do Instagram selecionada(s) na Meta: <span className="font-mono">{igIds.join(', ')}</span></p>
                )}
                <p className="font-semibold text-foreground pt-1">Como resolver:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Acesse <a href="https://business.facebook.com/settings/pages" target="_blank" rel="noreferrer" className="underline text-primary">business.facebook.com/settings/pages</a> com o usuário Facebook que você usou no login.</li>
                  <li>Verifique se a Página do Facebook vinculada ao Instagram aparece com perfil <span className="font-semibold">Administrador</span> ou <span className="font-semibold">Acesso total</span> — e não apenas via "Acesso por Business".</li>
                  <li>Em <span className="font-mono">Meta Business Suite → Configurações → Contas do Instagram</span>, confirme que o IG está vinculado à Página do Facebook correta.</li>
                  <li>Se a Página continua invisível, peça ao admin atual para conceder acesso direto de Admin ao seu usuário Facebook.</li>
                  <li>Reconecte aqui após ajustar os acessos.</li>
                </ol>
              </div>
            )}

            {/* Technical details intentionally NOT shown to clients.
                Server logs (edge function console.error) are the source of truth for support. */}

            <button
              onClick={() => window.close()}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Fechar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
